import React, { useState } from 'react';
import { SkinLightDiagram } from './SkinLightDiagram';
const lightBands = [{
  wavelength: '460',
  name: 'Blue LED',
  color: '#2867B5',
  tint: '#EDF4FE',
  focus: 'Near The Surface',
  body: 'Blue light interacts mainly nearer the skin’s surface. Blue-light devices are studied in acne care.'
}, {
  wavelength: '665',
  name: 'Red laser',
  color: '#B43D37',
  tint: '#FCEEEA',
  focus: 'Texture & Fine Lines',
  body: 'Red light travels below the surface and is studied for skin texture and the appearance of fine lines.'
}, {
  wavelength: '850',
  name: 'Near-infrared laser',
  color: '#7050A4',
  tint: '#F3EEFA',
  focus: 'Beyond Visible Light',
  body: 'Invisible near-infrared light can travel farther into tissue than shorter visible wavelengths.'
}, {
  wavelength: '1064',
  name: 'Near-infrared laser',
  color: '#87612B',
  tint: '#F6F0E5',
  focus: 'A Longer NIR Wavelength',
  body: 'Its interaction with skin depends on the device’s output and individual skin characteristics.'
}] as const;
type Wavelength = typeof lightBands[number]['wavelength'];
export function LightScience() {
  const [selected, setSelected] = useState<Wavelength>('665');
  const active = lightBands.find(band => band.wavelength === selected)!;
  return <section id="light-science" aria-labelledby="light-science-title" className="border-t border-line bg-white" data-id="element-214" data-mp="13goyfxo5k2p">
      <div className="mx-auto max-w-page px-5 py-16 md:px-10 lg:py-24" data-id="element-215" data-mp="55bmrage5fbq">
        <div className="grid gap-5 lg:grid-cols-[1fr_1fr] lg:items-end lg:gap-16" data-id="element-216" data-mp="a3d6lxln5io9">
          <div data-id="element-217" data-mp="a5g75bkf6u78">
            <p className="text-[13px] font-medium uppercase tracking-[.12em] text-bronze-deep" data-id="element-218" data-mp="f2zl0ad7h4yn">A closer look at light & skin</p>
            <h2 id="light-science-title" className="mt-4 max-w-xl font-display text-[42px] font-medium leading-[1.04] text-ink md:text-[58px]" data-id="element-219" data-mp="k0rlhsrx1zeg">Light, Beneath<br data-id="element-220" data-mp="2gw5sa2ir7aww" />The Surface.</h2>
          </div>
          <p className="max-w-xl text-[16px] leading-relaxed text-ink-muted" data-id="element-221" data-mp="2lse8lj8ota5n">See how blue, red and near-infrared light interact with the skin’s layers. This cutaway shows light spreading and fading as it travels into tissue.</p>
        </div>

        <figure data-skin-cross-section className="mt-10 overflow-hidden border border-line bg-[#FBF7F1]" data-id="element-222" data-mp="28vhrg6dyah3y">
          <div className="hidden px-4 pt-7 md:block md:px-7" data-id="element-223" data-mp="2nob6jqpb8v83">
            <SkinLightDiagram data-id="element-224" data-mp="2iqru8wc6asis" />
          </div>

          <div className="md:hidden" data-id="element-225" data-mp="2wrcb3myj2e9n">
            <div className="grid grid-cols-2 gap-2 px-4 pt-5" role="group" aria-label="Explore light through skin" data-id="element-226" data-mp="2wqapwpnssbe7">
              {lightBands.map(band => <button type="button" key={band.wavelength} data-skin-choice={band.wavelength} aria-pressed={selected === band.wavelength} aria-controls="skin-mobile-view" onClick={() => setSelected(band.wavelength)} className="min-h-[48px] rounded-sm border px-3 py-2 text-left text-[12px] font-medium leading-snug focus-visible:outline-offset-2" style={{
              color: selected === band.wavelength ? band.color : '#555852',
              backgroundColor: selected === band.wavelength ? band.tint : '#FFFFFF',
              borderColor: selected === band.wavelength ? band.color : '#E2DCCF'
            }} data-id="element-227" data-mp="36h5mamurcrdd">
                  <span className="block font-semibold" data-id="element-228" data-mp="3l4nmb7xdkxtg">{band.wavelength} nm</span>
                  <span data-id="element-229" data-mp="3q2flmn0cnm7o">{band.name}</span>
                </button>)}
            </div>
            <div id="skin-mobile-view" data-skin-selected={selected} className="pt-5" data-id="element-230" data-mp="3bdj664vyr6x1">
              <p className="px-5 text-[13px] font-semibold" style={{
              color: active.color
            }} data-id="element-231" data-mp="3g4ho18wlod2e">{active.wavelength} nm · {active.name}</p>
              <SkinLightDiagram selected={selected} compact data-id="element-232" data-mp="32ee6uwckv9jt" />
            </div>
          </div>

          <figcaption className="flex flex-wrap justify-between gap-x-6 gap-y-2 border-t border-line bg-white/60 px-5 py-4 text-[12px] leading-relaxed text-ink-muted md:px-7" data-id="element-233" data-mp="36fzsb636gw5m">
            <span data-id="element-234" data-mp="2xet7ikthwz6k">Conceptual skin illustration · Not to scale</span>
            <span data-id="element-235" data-mp="2xg4bakmxy6xo">Violet and gold illustrate invisible near-infrared light.</span>
          </figcaption>
        </figure>

        <div className="mt-7 hidden grid-cols-4 gap-6 md:grid" data-skin-context data-id="element-236" data-mp="3vqclkbawh68x">
          {lightBands.map(band => <article key={band.wavelength} data-skin-effect={band.wavelength} className="border-t-2 pt-4" style={{
          borderColor: band.color
        }} data-id="element-237" data-mp="3qykqbngsuccg">
              <p className="text-[12px] font-semibold" style={{
            color: band.color
          }} data-id="element-238" data-mp="3m0173utlbens">{band.wavelength} nm · {band.name}</p>
              <h3 className="mt-3 font-display text-[26px] font-medium leading-tight text-ink" data-id="element-239" data-mp="3c23gx1s677sj">{band.focus}</h3>
              <p className="mt-3 text-[14px] leading-relaxed text-ink-muted" data-id="element-240" data-mp="3808f7w6buse8">{band.body}</p>
            </article>)}
        </div>

        <div className="mt-6 border-l-2 pl-4 md:hidden" data-skin-mobile-context aria-live="polite" aria-atomic="true" style={{
        borderColor: active.color
      }} data-id="element-241" data-mp="332qg0xi43rjf">
          <h3 className="font-display text-[28px] font-medium leading-tight text-ink" data-id="element-242" data-mp="11abt1k0pnww5">{active.focus}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-243" data-mp="18u34wrmh361k">{active.body}</p>
        </div>

        <div className="mt-7 border-t border-line pt-5" data-id="element-244" data-mp="1ds4l94sikr9c">
          <p className="max-w-3xl text-[13px] leading-relaxed text-ink-muted" data-id="element-245" data-mp="1dql65hilgm95">General light-therapy research, not measured penetration or proven results for this mask. Light distribution and results depend on the device, treatment and individual skin.</p>
          <p className="mt-2 text-[12px] leading-relaxed text-ink-muted" data-id="element-246" data-mp="pvqw56zh3y6i">Research: <a href="https://pubmed.ncbi.nlm.nih.gov/32356424/" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-247" data-mp="utjytq1kia21">Light transport in skin</a> · <a href="https://www.aad.org/public/cosmetic/safety/red-light-therapy" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-248" data-mp="zuzmlpv4quo4">Red-light skincare</a> · <a href="https://www.aad.org/public/diseases/acne/derm-treat/lasers-lights" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-249" data-mp="2b7sagpksuyyw">Light-based acne care</a></p>
        </div>
      </div>
    </section>;
}